<template>
  <modal
    :bodyPadding="!showProcessing ? 0 : undefined"
    width="874px"
    :footerPadding="showProcessing ? 0 : undefined"
    :class="contentClass"
    :title="t('Payment required')"
    bodyOverflow="auto"
    :actionBeforeLogout="actionBeforeLogout"
    :logOutBtn="true"
    :closeBtn="false"
  >
    <div class="wrapper">
      <div v-if="showProcessing" class="processing-text">
        <b>{{ t('Your payment is processing by our system. Please wait.') }}<span>.</span><span>.</span>
        </b>
      </div>
      <div v-if="!showProcessing" class="left">
        <div class="main-title">{{ t('Your subscription is not paid.') }}</div>
        <div class="main-text">
          <p>{{ t('To restore the access to panel make sure the sufficient amount is available on the selected payment method or add other payment method.') }}</p>
          <p>{{ t('For assistance click on') }} <a @click="openLiveSupport">{{ t('Live Support') }}</a></p>
        </div>
        <loading-circle
          v-if="loadingInvoices"
          width="32"
          height="32"
          class="mt-16 mb-16"
        />
        <invoices-table
          v-else
          :unpaidData="unpaidData"
          :sumFormat="sumFormat"
          :totalUnpaid="totalUnpaid"
        />
        <div>
          <div class="main-title">{{ t('Billing Details') }}</div>
          <ui-select
            v-model="billingInfo.country"
            :items="countryList"
            :placeholder="t('Country')"
            :label="`${t('Country')}*`"
            item-text="name"
            item-value="code"
            name="country"
            :return-object="false"
            class="country-select"
            :error-messages="errorsDict['country']"
            max-width-calc
          />
        </div>
        <additional-info />
      </div>
      <div v-if="!showProcessing" class="right">
        <div class="main-title">{{ t('Payment Metod') }}</div>
        <button
          v-if="last4NumbersOfExistedCard"
          :disabled="disabled"
          type="button"
          class="payment-method"
          :class="{ active: paymentMethod === 'defaultCard' }"
          @click="selectPaymentMethod('defaultCard')"
        >
          ****{{ last4NumbersOfExistedCard }}
          <span class="payment-method__default">{{ t('Default') }}</span>
        </button>
        <button
          :disabled="disabled"
          type="button"
          class="payment-method"
          :class="{ active: paymentMethod === 'newCard' }"
          @click="selectPaymentMethod('newCard')"
        >
          {{ t('New Credit Card') }}
          <ui-icon color="#655cfb" size="16" icon="fas fa-credit-card" />
        </button>
        <div class="new-card__data" v-show="paymentMethod === 'newCard'">
          <loading-circle
            v-if="loadingStripe"
            width="32"
            height="32"
            class="stripeLoading"
          />
          <template v-else>
            <ui-label>{{ t('Card Number') }}</ui-label>
            <div ref="cardNumberRef" class="payment_input"></div>
            <div class="card-details">
              <div>
                <ui-label>{{ t('Expiration Date') }}</ui-label>
                <div ref="cardExpiryRef" class="payment_input"></div>
              </div>
              <div class="ml-16">
                <ui-label class="text-uppercase">{{ t('CVV / CVC') }}</ui-label>
                <div ref="cardCvcRef" class="payment_input"></div>
              </div>
            </div>
          </template>
        </div>
        <template v-if="internalPaymentGatewayEnabled">
          <button
            :disabled="disabled"
            type="button"
            class="payment-method"
            @click="selectPaymentMethod('paypal')"
            :class="{ active: paymentMethod === 'paypal' }"
          >
            PayPal
            <img alt="wechat" height="18" src="/images/payment/PayPal-small.svg" />
          </button>
          <div class="qr-code" id="wechat-qrcode"></div>
          <button
            :disabled="disabled"
            type="button"
            class="payment-method"
            @click="selectPaymentMethod('alipay')"
            :class="{ active: paymentMethod === 'alipay' }"
          >
            AliPay
            <img alt="alipay" height="18" src="/images/payment/alipay.svg" />
          </button>
        </template>
      </div>
    </div>

    <template #footer v-if="!showProcessing">
      <section class="w100 footer-content">
        <error-aggregator
          class="aggregator-wrap"
          v-model="errors"
          v-model:active="errorsControlActive"
        />
        <a href="https://stripe.com/" target="_blank" class="powered-by-stripe">
          <img src="/images/payment/powered-by-stripe.svg" alt="powered by stripe" />
        </a>
        <div class="pay-button-wrp">
          <div
            v-show="paymentMethod === 'paypal'"
            id="paypal-button-container"
            class="paypal-button-container"
          ></div>
          <ui-button
            :disabled="disabled || loadingStripe"
            v-if="paymentMethod !== 'paypal'"
            :text="payButtonText"
            @click="pay"
            :loading="dialogsApi.loading"
            button-style="accent"
          />
        </div>
      </section>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import AdditionalInfo from './additional-info.vue';
import InvoicesTable from './invoices-table.vue';
import LoadingCircle from '@/components/loading-circle.vue';
import { useUnpaidInvoicesDialog } from './useUnpaidInvoicesDialog';

const cardNumberRef = ref<HTMLElement | null>(null);
const cardExpiryRef = ref<HTMLElement | null>(null);
const cardCvcRef = ref<HTMLElement | null>(null);

const {
  t,
  countryList,
  dialogsApi,
  last4NumbersOfExistedCard,
  paymentMethod,
  loadingInvoices,
  loadingStripe,
  showProcessing,
  disabled,
  unpaidData,
  billingInfo,
  errors,
  errorsControlActive,
  errorsDict,
  openLiveSupport,
  contentClass,
  payButtonText,
  totalUnpaid,
  internalPaymentGatewayEnabled,
  selectPaymentMethod,
  sumFormat,
  pay,
  actionBeforeLogout,
} = useUnpaidInvoicesDialog({
  cardNumber: cardNumberRef,
  cardExpiry: cardExpiryRef,
  cardCvc: cardCvcRef,
});
</script>

<style lang="scss" scoped>
.wrapper{
  display: flex;
}
.main-title{
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 4px;
}

.main-text{
  line-height: 22px;
  :deep(p){
    margin-bottom: 0;
  }
}

.left{
  background: var(--color-gray3);
  padding:16px 32px 16px 16px;
  box-sizing: border-box;
  flex-direction: column;
  display: flex;
}

.right{
  padding:16px 24px 16px 24px;
  width:302px;
  box-sizing: border-box;
  flex-shrink: 0;
}

.stripeLoading{
  height:auto;
  margin:24px 0 0 0;
}

.payment-method{
  border-radius: 8px;
  margin: 0 0 8px 0;
  position: relative;
  padding: 10px 12px 10px 32px;
  border:1px solid var(--color-gray10);
  width:100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  &.active{
    border-color:var(--color-green);
    &:before{
      border: solid 5px var(--color-green);
    }
  }
  &:disabled{
    opacity: 0.6;
  }
  &:before{
    position:absolute;
    left:12px;
    content: '';
    width: 16px;
    display: block;
    height: 16px;
    margin: 0 4px 0 0;
    border-radius: 50%;
    border: solid 1px var(--color-gray20);
  }
}

.payment-method__default{
  font-size: 13px;
  color:var(--color-green);
  font-weight: bold;
}

.new-card__data{
  margin:14px 0 24px;
}

.powered-by-stripe{
  flex:0 0 auto!important;
  align-self: center;
  img{
    display: block;
  }

  @media (max-width: 600px) {
    display: none;
  }
}

.footer-content{
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.pay-button-wrp{
  min-width: 200px;
  display: flex;
  justify-content: right;
}

.paypal-button-container{
  display: flex;
}

.payment_input{
  border-radius: 6px;
  padding: 0 5px;
  margin-top:6px;
  border: solid 1px var(--color-gray20);
}

.card-details{
  display: flex;
  padding-top:16px;
  justify-content: space-between;
  &>div{
    width:calc(100% - 8px)
  }
}

.country-select{
  width:300px;
}

.internal-title{
  font-size: 14px;
}
.internal-methods{
  display: flex;
  justify-content:flex-end;
  button{
    width:120px;
    flex-grow: 0!important;
    min-width: 90px!important;
    &:disabled{
      opacity: 0.2;
    }
  }
}

.internal-methods-paypal{
  width: 120px;
  overflow: hidden;
  flex-grow: 0!important;
  border-radius: 5px;
  height: 34px;
  flex-shrink: 0!important;
  :deep(.paypal-buttons){
    margin-left:-15px;
  }
}

.internal-methods-paypal__disabled{
  opacity: 0.2;
  pointer-events: none;
}

.internal-methods--button{
  background: var(--color-gray10);
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.main-methods{
  justify-content:flex-end;
  button{
    flex-grow: 0!important;
  }
}

.qr-code{
  display:flex;
  justify-content: center;
  :deep(img){
    margin-top:8px!important;
    margin-bottom:16px!important;
  }
}

.secure-text{
  font-size: 12px!important;
}
</style>

<style lang="scss" scoped>
  @keyframes unpaid-invoices-dialog_blink {
    0% {
      opacity: 0;
    }
    25% {
      opacity: 1;
    }
  }

  @keyframes unpaid-invoices-dialog_blink2 {
    0% {
      opacity: 0;
    }
    50% {
      opacity: 1;
    }
  }
  .unpaid-invoices-dialog{
    .processing-text{
      font-size: 14px;
      padding:8px 0 16px;

      span:nth-child(1) {
        animation-delay: .2s;
        animation: unpaid-invoices-dialog_blink 1.5s infinite steps(1, end);
      }

      span:nth-child(2) {
        animation-delay: .4s;
        animation: unpaid-invoices-dialog_blink2 1.5s infinite steps(1, end);
      }
    }
  }
</style>
